import { PublicClientApplication, type Configuration, LogLevel } from '@azure/msal-browser';

/**
 * MSAL configuration for the AES I.T. Helpdesk SPA.
 *
 * Authority is single-tenant — only aesintl.com users can sign in. The redirect
 * URIs we register in Entra (http://localhost:5173/auth/callback and
 * https://helpdesk.aesintl.com/auth/callback) must be reachable for sign-in to
 * complete.
 *
 * localStorage cache: the signed-in account + tokens survive closing the tab /
 * browser, so the app behaves like a desktop app that "remembers you" — combined
 * with the auto sign-in in Login.tsx, a returning user lands straight in without
 * a prompt. (sessionStorage would wipe the session on tab close, forcing a fresh
 * sign-in every launch.) Tokens live in this browser profile only and are cleared
 * on Sign out; refresh tokens for SPAs are short-lived, so the exposure window is
 * bounded.
 */
const config: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_ENTRA_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_ENTRA_TENANT_ID}`,
    redirectUri: `${window.location.origin}/auth/callback`,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false
  },
  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
      piiLoggingEnabled: false,
      loggerCallback(level, message, containsPii) {
        if (containsPii) return;
         
        if (level === LogLevel.Error) console.error('[msal]', message);
        else if (level === LogLevel.Warning) console.warn('[msal]', message);
      }
    }
  }
};

/** Scopes for sign-in (just identity). */
export const loginRequest = {
  scopes: ['openid', 'profile', 'email']
};

/** Scopes for calling our own API. Cached separately by MSAL per scope set. */
export const apiRequest = {
  scopes: [import.meta.env.VITE_ENTRA_API_SCOPE]
};

export const msalInstance = new PublicClientApplication(config);

/**
 * sessionStorage flag set when the user explicitly signs out, so the Login
 * screen doesn't immediately auto sign them back in (that would make "Sign out"
 * impossible). Cleared when they click "Sign in" again. Lives in sessionStorage
 * so it's scoped to the tab and never outlives the browser.
 */
export const SIGNED_OUT_FLAG = 'aes-signed-out';
